import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { ArrowRight, Bot, Check, Columns3, Minus, Package, Star } from "lucide-react";

const euro = (value) => `${(Number(value) || 0).toFixed(2).replace(".", ",").replace(",00", "")} €`;
const MAX_COLUMNS = 4;

const lifetimePlan = (product) => (product.plans || []).find((p) => p.id === "lifetime");
const cheapestPlan = (product) => (product.plans || [])
  .filter((p) => p.id !== "lifetime")
  .sort((a, b) => (a.duration_days || 0) - (b.duration_days || 0))[0];

/** Choix par défaut : un pack et ses produits s'il y en a un, sinon les premiers produits. */
function defaultSelection(products) {
  const bundle = products.find((p) => p.includes?.length);
  if (bundle) {
    const ids = [...bundle.includes.filter((id) => products.some((p) => p.id === id)), bundle.id];
    return ids.slice(-MAX_COLUMNS);
  }
  return products.slice(0, MAX_COLUMNS).map((p) => p.id);
}

export default function ComparePage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [ratings, setRatings] = useState({});

  useEffect(() => {
    axios.get(`${API}/shop/bots`).then((r) => {
      const list = r.data || [];
      setProducts(list);
      setSelected(defaultSelection(list));
      list.forEach((p) => {
        axios.get(`${API}/reviews/stats`, { params: { product_id: p.id } })
          .then((s) => setRatings((current) => ({ ...current, [p.id]: s.data })))
          .catch(() => {});
      });
    }).catch(() => {});
  }, []);

  const byId = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const columns = selected.map((id) => byId[id]).filter(Boolean);

  const toggle = (id) => {
    if (selected.includes(id)) setSelected(selected.filter((s) => s !== id));
    else if (selected.length < MAX_COLUMNS) setSelected([...selected, id]);
  };

  // Un pack a aussi les fonctionnalités des produits qu'il contient.
  const featuresOf = (product) => {
    const own = product.features || [];
    const included = (product.includes || []).flatMap((id) => byId[id]?.features || []);
    return new Set([...own, ...included].map((f) => (typeof f === "string" ? f : f.title)));
  };
  const allFeatures = [...new Set(columns.flatMap((p) => [...featuresOf(p)]))];

  const savings = (bundle, planOf) => {
    if (!bundle.includes?.length) return null;
    const bundlePlan = planOf(bundle);
    const parts = bundle.includes.map((id) => byId[id] && planOf(byId[id]));
    if (!bundlePlan || parts.some((p) => !p)) return null;
    const separate = parts.reduce((sum, p) => sum + p.price, 0);
    return separate > bundlePlan.price ? { separate, saved: separate - bundlePlan.price } : null;
  };

  const rows = [
    {
      label: "Abonnement",
      render: (p) => {
        const plan = cheapestPlan(p);
        return plan ? <span>{euro(plan.price)} <span className="text-text-muted text-xs">/ {plan.duration_label || `${plan.duration_days} j`}</span></span> : <Minus className="w-4 h-4 text-white/15 mx-auto" />;
      },
    },
    {
      label: "À vie",
      render: (p) => {
        const plan = lifetimePlan(p);
        return plan ? <span className="font-semibold">{euro(plan.price)}</span> : <Minus className="w-4 h-4 text-white/15 mx-auto" />;
      },
    },
    {
      label: "Économie du pack",
      hidden: !columns.some((p) => p.includes?.length),
      render: (p) => {
        const life = savings(p, lifetimePlan);
        const month = savings(p, cheapestPlan);
        if (!life && !month) return <Minus className="w-4 h-4 text-white/15 mx-auto" />;
        return (
          <div className="text-emerald-400 text-sm space-y-0.5">
            {month && <p>-{euro(month.saved)} par mois</p>}
            {life && <p>-{euro(life.saved)} à vie <span className="text-text-muted text-xs">(au lieu de {euro(life.separate)})</span></p>}
          </div>
        );
      },
    },
    {
      label: "Contient",
      hidden: !columns.some((p) => p.includes?.length),
      render: (p) => (p.includes?.length ? <span className="text-sm">{p.includes.map((id) => byId[id]?.name || id).join(" + ")}</span> : <Minus className="w-4 h-4 text-white/15 mx-auto" />),
    },
    {
      label: "Essai gratuit",
      render: (p) => (p.trial_days > 0 ? <span className="text-emerald-400">{p.trial_days} jours</span> : <Minus className="w-4 h-4 text-white/15 mx-auto" />),
    },
    {
      label: "Type",
      render: (p) => <span className="text-sm">{p.downloadable ? "Script (clé de licence)" : "Bot Discord hébergé"}</span>,
    },
    {
      label: "Version",
      hidden: !columns.some((p) => p.current_version),
      render: (p) => (p.current_version ? `v${p.current_version}` : <Minus className="w-4 h-4 text-white/15 mx-auto" />),
    },
    {
      label: "Avis clients",
      render: (p) => {
        const r = ratings[p.id];
        return r?.count ? <span className="inline-flex items-center gap-1"><Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />{r.average.toFixed(1)} <span className="text-text-muted text-xs">({r.count})</span></span> : <span className="text-text-muted text-xs">Pas encore d'avis</span>;
      },
    },
  ].filter((row) => !row.hidden);

  return (
    <div className="min-h-screen bg-vanta-base">
      <Navbar />
      <main className="relative">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[360px] overflow-hidden">
          <div className="absolute left-1/2 top-[-180px] h-[380px] w-[860px] -translate-x-1/2 rounded-full bg-gradient-to-r from-violet-600/20 via-blue-600/20 to-cyan-500/15 blur-[90px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 pb-20 pt-28 sm:px-6 lg:px-8">
          <div className="page-rise text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/65">
              <Columns3 className="h-3.5 w-3.5 text-violet-300" />Comparateur
            </span>
            <h1 className="mt-4 font-heading text-4xl font-bold tracking-tight sm:text-5xl">Comparer les produits</h1>
            <p className="mx-auto mt-3 max-w-xl text-[15px] text-white/50">Choisis jusqu'à {MAX_COLUMNS} produits pour voir leurs différences, leurs prix et ce que tu économises avec un pack.</p>
          </div>

          <div className="page-rise mt-10 flex flex-wrap justify-center gap-2" data-testid="compare-picker" style={{ animationDelay: "60ms" }}>
            {products.map((p) => {
              const on = selected.includes(p.id);
              return (
                <button
                  key={p.id}
                  onClick={() => toggle(p.id)}
                  disabled={!on && selected.length >= MAX_COLUMNS}
                  className={`inline-flex items-center gap-2 rounded-xl border py-1.5 pl-1.5 pr-3.5 text-sm transition-all disabled:opacity-35 ${on ? "border-blue-400/50 bg-blue-500/[0.12] text-white shadow-[0_0_0_3px_rgba(59,130,246,0.1)]" : "border-white/[0.08] bg-white/[0.03] text-white/60 hover:border-white/20 hover:text-white"}`}
                >
                  <span className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-lg bg-white/[0.06]">
                    {p.image_url ? <img src={p.image_url} alt="" className="h-full w-full object-cover" /> : (p.downloadable ? <Package className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />)}
                  </span>
                  {p.name}
                  <span className={`ml-0.5 flex h-4 w-4 items-center justify-center rounded-[5px] border ${on ? "border-blue-400 bg-blue-500" : "border-white/20"}`}>
                    {on && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-center text-xs text-white/30">{selected.length}/{MAX_COLUMNS} sélectionnés</p>

          {columns.length === 0 ? (
            <div className="mt-10 rounded-2xl border border-dashed border-white/10 py-14 text-center text-sm text-white/40">Sélectionne au moins un produit.</div>
          ) : (
            <div className="page-rise mt-8 overflow-x-auto rounded-3xl border border-white/[0.08] bg-[#0f1019]" data-testid="compare-table" style={{ animationDelay: "120ms" }}>
              <table className="w-full min-w-[680px] text-sm">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 w-52 bg-[#0f1019] p-5 text-left align-bottom">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/30">Produits</span>
                    </th>
                    {columns.map((p) => {
                      const plan = cheapestPlan(p) || lifetimePlan(p);
                      const color = p.color || "#3b82f6";
                      return (
                        <th key={p.id} className="p-4 align-top">
                          <div className="relative overflow-hidden rounded-2xl border border-white/[0.07] bg-white/[0.02] px-4 pb-4 pt-5 text-center">
                            <div className="pointer-events-none absolute inset-x-0 top-0 h-16 opacity-60" style={{ background: `radial-gradient(80% 100% at 50% 0%, ${color}55, transparent)` }} />
                            <div className="relative mx-auto flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-[#0b0c14]" style={{ boxShadow: `0 10px 28px -12px ${color}` }}>
                              {p.image_url ? <img src={p.image_url} alt="" className="h-full w-full object-cover" /> : <Bot className="h-6 w-6" style={{ color }} />}
                            </div>
                            <p className="relative mt-3 font-heading text-[15px] font-bold leading-tight">{p.name}</p>
                            {plan && <p className="relative mt-1 text-xs font-normal text-white/45">dès <span className="font-semibold text-white">{euro(plan.price)}</span> / {plan.duration_label || `${plan.duration_days} j`}</p>}
                            <Button size="sm" onClick={() => navigate(`/bot/${p.id}`)} className="relative mt-4 h-9 w-full rounded-xl text-xs font-semibold text-white hover:brightness-110" style={{ background: `linear-gradient(135deg, ${color}, ${color}bb)` }}>
                              Voir le produit<ArrowRight className="ml-1 h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.label} className={`border-t border-white/[0.05] ${index % 2 ? "bg-white/[0.012]" : ""}`}>
                      <td className="sticky left-0 z-10 bg-[#0f1019] px-5 py-4 font-medium text-white/60">{row.label}</td>
                      {columns.map((p) => <td key={p.id} className="px-4 py-4 text-center text-white/85">{row.render(p)}</td>)}
                    </tr>
                  ))}
                  {allFeatures.length > 0 && (
                    <tr className="border-t border-white/[0.06]">
                      <td colSpan={columns.length + 1} className="bg-white/[0.03] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/45">Fonctionnalités</td>
                    </tr>
                  )}
                  {allFeatures.map((feature, index) => (
                    <tr key={feature} className={`border-t border-white/[0.05] transition-colors hover:bg-white/[0.02] ${index % 2 ? "bg-white/[0.012]" : ""}`}>
                      <td className="sticky left-0 z-10 bg-[#0f1019] px-5 py-3.5 text-[13px] leading-snug text-white/60">{feature}</td>
                      {columns.map((p) => (
                        <td key={p.id} className="px-4 py-3.5 text-center">
                          {featuresOf(p).has(feature)
                            ? <span className="mx-auto flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/15"><Check className="h-3.5 w-3.5 text-emerald-300" strokeWidth={3} /></span>
                            : <Minus className="w-4 h-4 text-white/15 mx-auto" />}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
